import { useEffect, useState } from "react";
import { api, Book, getHoldingsPageScope, getLedgerPageBook, Manifest, Me, setActiveBook, takeNav } from "../api";
import Firm from "./Firm";
import Command from "./Command";
import Signals from "./Signals";
import BullSheet from "./BullSheet";
import Plan from "./Plan";
import Holdings from "./Holdings";
import Ledger from "./Ledger";
import Commodities from "./Commodities";
import Tax from "./Tax";
import Funding from "./Funding";
import Boardroom from "./Boardroom";
import Constitution from "./Constitution";
import Risk from "./Risk";
import GraphExplorer from "./GraphExplorer";
import ConfigStudio from "./ConfigStudio";
import Connections from "./Connections";
import Podcast from "./Podcast";
import Control from "./Control";
import AdvisorChat from "./AdvisorChat";
import Intel from "./Intel";

// Navigation structure is CODE (it is the firm's information architecture);
// the manifest's panels config controls only visibility and titles where an
// id matches. One firm · four books · three sleeves — the BOOK is the frame,
// pages are functions of it, asset classes are rows inside pages.
//
// FIRM comes first and is where you land: the owner used to open on Command,
// which is ONE book's slice, so the landing number was never what he actually
// owned. Whole first, then drill down.
const ALL_VIEWS: Record<string, (p: { manifest: Manifest }) => JSX.Element> = {
  firm: Firm,
  command: Command,
  signals: Signals,
  intel: Intel,
  bullsheet: BullSheet,
  plan: Plan,
  holdings: Holdings,
  ledger: Ledger,
  tax: Tax,
  commodities: Commodities,
  funding: Funding,
  boardroom: Boardroom,
  risk: Risk,
  constitution: Constitution,
  graph: GraphExplorer,
  config: ConfigStudio,
  connections: Connections,
  podcast: Podcast,
  control: Control,
};

const PRIMARY = ["firm", "command", "signals", "intel", "bullsheet", "plan", "holdings", "commodities", "ledger", "tax", "funding"];
const FIRM_GROUP = ["boardroom", "risk", "control", "constitution", "graph", "config", "connections", "podcast"];
const TITLES: Record<string, string> = {
  firm: "The Firm",
  command: "Command", signals: "Signals", intel: "Market Intel", bullsheet: "The Bull Sheet", plan: "Plan",
  holdings: "Holdings", commodities: "Commodities", ledger: "Performance", tax: "Tax Desk", funding: "Funding & Dials",
  boardroom: "Boardroom", risk: "Risk & Limits", control: "Control Panel", constitution: "Constitution",
  graph: "Graph Explorer", config: "Config Studio", connections: "Connections",
  podcast: "After Hours",
};
// Which pages read the ACTIVE BOOK vs the whole firm — surfaced as a chip on
// every page so book-scoping is never invisible again.
const SCOPE: Record<string, "book" | "firm"> = {
  command: "book", plan: "book", holdings: "book", commodities: "book", ledger: "book", boardroom: "book", risk: "book",
  firm: "firm",
  signals: "firm", intel: "firm", bullsheet: "firm", tax: "firm", funding: "firm", constitution: "firm",
  graph: "firm", config: "firm", connections: "firm", control: "firm",
  podcast: "firm",
};
// Mobile bottom tabs — the landing view + the four everyday pages + More.
const MOBILE_TABS: [string, string][] = [
  ["firm", "Firm"], ["command", "Cmd"], ["plan", "Plan"], ["holdings", "Held"], ["bullsheet", "Sheet"],
];

export const BOOK_META: Record<string, { label: string; short: string; mode: "paper" | "live" }> = {
  paper: { label: "Paper · Daily", short: "P·D", mode: "paper" },
  live: { label: "Live · Daily", short: "L·D", mode: "live" },
  paper_long_term: { label: "Paper · Growth", short: "P·G", mode: "paper" },
  live_long_term: { label: "Live · Growth", short: "L·G", mode: "live" },
};
const BOOK_ORDER = ["paper", "live", "paper_long_term", "live_long_term"];

export default function Layout({
  me,
  manifest,
  onLogout,
}: {
  me: Me;
  manifest: Manifest;
  onLogout: () => void;
}) {
  const [active, setActive] = useState("firm");
  const [navOpen, setNavOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [books, setBooks] = useState<Book[]>([]);
  // Opens to the user's saved login preference (server-side, per-user — see
  // PUT /api/auth/default-book), falling back to live if never set. Alan
  // 2026-08-04: wanted live-by-default with the ability to change it later.
  const [defaultBook, setDefaultBook] = useState(me.default_book || "live");
  // Lazy initializer, so activeBook is pointed at this book DURING the first
  // render — before any child effect fires a fetch. Doing it in a useEffect
  // (as this used to) is too late: child effects run before the parent's.
  const [book, setBook] = useState(() => {
    setActiveBook(defaultBook);
    return defaultBook;
  });

  useEffect(() => {
    const tick = () => api.get<Book[]>("/api/trading/books").then(setBooks).catch(() => {});
    tick();
    const iv = setInterval(tick, 20000);
    return () => clearInterval(iv);
  }, []);

  // Set the active book SYNCHRONOUSLY on switch — before the keyed <View>
  // remounts and refetches — so a child never loads with a stale book.
  const pickBook = (k: string) => { setActiveBook(k); setBook(k); setBookOpen(false); };

  const [savingDefault, setSavingDefault] = useState(false);
  const makeDefault = async () => {
    setSavingDefault(true);
    try { await api.put("/api/auth/default-book", { book }); setDefaultBook(book); }
    catch { /* transient — the button just stays visible to retry */ }
    finally { setSavingDefault(false); }
  };

  // Trade Story's "View full graph" — see focusGraph/takeGraphFocus in api.ts.
  useEffect(() => {
    const onNavigateGraph = () => setActive("graph");
    window.addEventListener("stonky:navigate-graph", onNavigateGraph);
    return () => window.removeEventListener("stonky:navigate-graph", onNavigateGraph);
  }, []);

  // The Firm view's drill-down: open a page, optionally on a specific book.
  // Routes the book through the SAME pickBook the selector uses, so the active
  // book is set synchronously before the keyed <View> remounts and refetches.
  useEffect(() => {
    const onOpenView = () => {
      const nav = takeNav();
      if (!nav) return;
      if (nav.book) pickBook(nav.book);
      setActive(nav.view);
      setNavOpen(false);
    };
    window.addEventListener("stonky:open-view", onOpenView);
    return () => window.removeEventListener("stonky:open-view", onOpenView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Ledger page owns its own book selector — the frame chip must read
  // THAT, not the sidebar's book it used to lie about (Kaizen D4/F5).
  const [ledgerBook, setLedgerBook] = useState(getLedgerPageBook());
  useEffect(() => {
    const onLedgerBook = () => setLedgerBook(getLedgerPageBook());
    window.addEventListener("stonky:ledger-book", onLedgerBook);
    return () => window.removeEventListener("stonky:ledger-book", onLedgerBook);
  }, []);

  // Holdings rolls up ACROSS books — same rule, same fix.
  const [holdScope, setHoldScope] = useState(getHoldingsPageScope());
  useEffect(() => {
    const onHold = () => setHoldScope(getHoldingsPageScope());
    window.addEventListener("stonky:holdings-scope", onHold);
    return () => window.removeEventListener("stonky:holdings-scope", onHold);
  }, []);

  const hidden = new Set(manifest.frontend.panels.filter((p) => !p.visible).map((p) => p.id));
  const titleOf = (id: string) =>
    manifest.frontend.panels.find((p) => p.id === id)?.title ?? TITLES[id] ?? id;

  const bookRow = books.find((b) => b.book_key === book);
  const meta = BOOK_META[book] ?? { label: book, short: book, mode: "paper" as const };
  const activeEnabled = bookRow?.enabled ?? (meta.mode === "paper");
  const liveOff = meta.mode === "live" && !activeEnabled;

  const View = ALL_VIEWS[active] ?? Command;
  const pick = (id: string) => { setActive(id); setNavOpen(false); };

  const ledgerMeta = BOOK_META[ledgerBook];
  const scopeChip = active === "ledger" ? (
    // Ledger's chip reflects the PAGE's own selector, not the sidebar book.
    ledgerBook === "all" ? (
      <span className="badge bg-gold/15 text-gold tracking-wider">ALL BOOKS — MIXED REAL+PAPER</span>
    ) : (
      <span className={`badge tracking-wider ${ledgerMeta?.mode === "live" ? "bg-down/15 text-down" : "bg-accent/15 text-accent"}`}>
        {(ledgerMeta?.label ?? ledgerBook).toUpperCase()}
      </span>
    )
  ) : active === "holdings" ? (
    // Holdings' chip reflects the PAGE's own scope: one book, or every book
    // of one money type. It must never show a red LIVE chip over a page that
    // is listing paper positions.
    holdScope === "real" ? (
      <span className="badge bg-down/15 text-down tracking-wider">ALL LIVE BOOKS — REAL MONEY</span>
    ) : holdScope === "paper" ? (
      <span className="badge bg-slate-700 text-slate-300 tracking-wider">ALL PAPER BOOKS — SIMULATED</span>
    ) : (
      <span className={`badge tracking-wider ${BOOK_META[holdScope]?.mode === "live" ? "bg-down/15 text-down" : "bg-accent/15 text-accent"}`}>
        {(BOOK_META[holdScope]?.label ?? holdScope).toUpperCase()}
      </span>
    )
  ) : SCOPE[active] === "firm" ? (
    <span className="badge bg-slate-800 text-slate-400 tracking-wider">FIRM-WIDE</span>
  ) : (
    <span className={`badge tracking-wider ${meta.mode === "live" ? "bg-down/15 text-down" : "bg-accent/15 text-accent"}`}>
      {meta.label.toUpperCase()}
    </span>
  );

  const navItems = (ids: string[]) =>
    ids.filter((id) => !hidden.has(id)).map((id) => (
      <NavItem key={id} id={id} title={titleOf(id)} active={active} onPick={pick}
               primary={id === "firm" || id === "command"} />
    ));

  return (
    <div className="min-h-screen md:flex">
      {/* Mobile top bar: menu · brand · book switcher */}
      <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b border-edge bg-ink/95 backdrop-blur">
        <button aria-label="Menu" onClick={() => setNavOpen(true)} className="text-slate-300 text-2xl leading-none">☰</button>
        <span className="font-black tracking-tight text-lg uppercase">Stonky</span>
        <button onClick={() => setBookOpen((o) => !o)}
                className={`ml-auto px-2.5 py-1 rounded-lg border text-xs font-bold ${
                  meta.mode === "live" ? "border-down text-down" : "border-accent text-accent"}`}>
          {meta.label} ▾
        </button>
      </div>
      {bookOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setBookOpen(false)}>
          <div className="absolute right-3 top-14 w-56 rounded-xl border border-edge bg-ink p-2 space-y-1"
               onClick={(e) => e.stopPropagation()}>
            {BOOK_ORDER.map((k) => <BookButton key={k} k={k} book={book} books={books} onPick={pickBook} />)}
          </div>
        </div>
      )}

      {/* Backdrop (mobile, when drawer open) */}
      {navOpen && <div className="md:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setNavOpen(false)} />}

      <aside className={`w-64 md:w-56 shrink-0 border-r border-edge p-4 flex flex-col gap-1
        fixed md:static inset-y-0 left-0 z-50 bg-ink md:bg-transparent overflow-y-auto
        transform transition-transform md:transform-none ${navOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="mb-3">
          <div className="font-black tracking-tight text-xl uppercase">Stonky</div>
          <div className="text-[9px] font-mono tracking-[0.18em] text-slate-500 uppercase mb-1">
            Flat by close
          </div>
          <div className="text-xs text-slate-500">{manifest.tenant}&rsquo;s firm</div>
        </div>

        {/* Book bar — the firm's four books, named. Everything below reads it. */}
        <div className="text-[10px] uppercase tracking-wider text-slate-600 px-1 mb-1">Book</div>
        <div className="grid grid-cols-2 gap-1 mb-1">
          {BOOK_ORDER.map((k) => <BookButton key={k} k={k} book={book} books={books} onPick={pickBook} compact />)}
        </div>
        <div className="text-[10px] text-slate-600 px-1 mb-3">
          {book === defaultBook ? (
            <span>opens here on login</span>
          ) : (
            <button className="underline hover:text-slate-400 disabled:opacity-50"
                    disabled={savingDefault} onClick={makeDefault}>
              {savingDefault ? "saving…" : `set ${BOOK_META[book]?.short ?? book} as default on login`}
            </button>
          )}
        </div>

        {navItems(PRIMARY)}
        <div className="text-[10px] uppercase tracking-wider text-slate-600 px-3 mt-4 mb-1">Firm</div>
        {navItems(FIRM_GROUP)}

        <div className="mt-auto pt-3 border-t border-edge space-y-2">
          <KillSwitch />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{me.username}</span>
            <button className="btn text-xs" onClick={onLogout}>Sign out</button>
          </div>
        </div>
      </aside>

      <main className="flex-1 p-4 md:p-6 overflow-x-hidden min-w-0 pb-20 md:pb-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {scopeChip}
          {liveOff && (
            <span className="text-xs text-slate-500">
              this live book is OFF — nothing trades here until you enable it in Risk &amp; Limits
            </span>
          )}
        </div>
        {meta.mode === "live" && activeEnabled && (
          <div className="mb-4 card border-down bg-down/10 text-down font-bold flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-down animate-pulse" />
            {meta.label.toUpperCase()} — real money. This book (incl. its crypto &amp; forex sleeves) trades funded accounts.
          </div>
        )}
        {manifest.halted && (
          <div className="mb-4 card border-down/60 text-down font-semibold">
            ⛔ FIRM HALTED — kill switch or risk halt active. See Risk &amp; Limits.
          </div>
        )}
        <View manifest={manifest} key={book} />
      </main>

      {/* Mobile bottom tabs */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 grid grid-cols-6 border-t border-edge bg-ink/95 backdrop-blur">
        {MOBILE_TABS.map(([id, label]) => (
          <button key={id} onClick={() => pick(id)}
                  className={`py-2.5 text-[11px] font-semibold ${active === id ? "text-accent" : "text-slate-500"}`}>
            <span className="block text-base leading-none mb-0.5">{TAB_ICONS[id]}</span>{label}
          </button>
        ))}
        <button onClick={() => setNavOpen(true)} className="py-2.5 text-[11px] font-semibold text-slate-500">
          <span className="block text-base leading-none mb-0.5">⋯</span>More
        </button>
      </nav>

      {/* Frame-level chrome: reachable from every page, survives navigation
          (sibling of <main>, not remounted when the routed View changes). */}
      <AdvisorChat />
    </div>
  );
}

const TAB_ICONS: Record<string, string> = { firm: "◆", command: "◉", plan: "▤", holdings: "▦", bullsheet: "▣" };

function BookButton({ k, book, books, onPick, compact }: {
  k: string; book: string; books: Book[]; onPick: (k: string) => void; compact?: boolean;
}) {
  const m = BOOK_META[k];
  const row = books.find((b) => b.book_key === k);
  const enabled = row?.enabled ?? (m.mode === "paper");
  const isActive = book === k;
  return (
    <button onClick={() => onPick(k)}
      className={`px-2 py-1.5 rounded-lg text-left text-xs font-semibold border transition-colors ${
        isActive
          ? (m.mode === "live" ? "border-down text-down bg-down/10" : "border-accent text-accent bg-accent/10")
          : "border-edge text-slate-400 hover:border-slate-500"}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${
        enabled ? (m.mode === "live" ? "bg-down animate-pulse" : "bg-up") : "bg-slate-600"}`} />
      {compact ? m.label.replace(" · ", "·") : m.label}
      {!enabled && <span className="text-slate-600 font-normal"> off</span>}
    </button>
  );
}

// Frame-level kill switch: always reachable, two-step (arm, then type-confirm).
function KillSwitch() {
  const [arm, setArm] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const PHRASE = "FLATTEN AND HALT";
  async function kill() {
    setBusy(true);
    try { await api.post("/api/risk/kill", { confirm }); setConfirm(""); setArm(false); }
    catch { /* Risk page shows detail */ }
    finally { setBusy(false); }
  }
  if (!arm) {
    return (
      <button className="w-full text-left px-2 py-1.5 rounded-lg border border-down/40 text-down text-xs font-bold hover:bg-down/10"
              onClick={() => setArm(true)}>
        ⛔ Kill switch
      </button>
    );
  }
  return (
    <div className="space-y-1.5 border border-down/40 rounded-lg p-2">
      <div className="text-[10px] text-slate-500">cancels all · flattens · halts</div>
      <input className="input w-full font-mono text-xs" placeholder={PHRASE} value={confirm}
             onChange={(e) => setConfirm(e.target.value)} />
      <div className="flex gap-1.5">
        <button className="btn btn-danger text-xs flex-1" disabled={busy || confirm !== PHRASE} onClick={kill}>
          {busy ? "…" : "KILL"}
        </button>
        <button className="btn text-xs" onClick={() => { setArm(false); setConfirm(""); }}>✕</button>
      </div>
    </div>
  );
}

function NavItem({
  id,
  title,
  active,
  onPick,
  primary,
}: {
  id: string;
  title: string;
  active: string;
  onPick: (id: string) => void;
  primary?: boolean;
}) {
  return (
    <button
      onClick={() => onPick(id)}
      className={`text-left px-3 py-2 rounded-lg transition-colors ${primary ? "text-base font-bold" : "text-sm"} ${
        active === id ? "bg-accent/15 text-accent font-semibold" : "hover:bg-panel text-slate-300"
      }`}
    >
      {title}
    </button>
  );
}
